import React, { useState } from 'react';
import { X } from 'lucide-react';
import { CustomKeyboardModal } from './CustomKeyboardModal';

interface SearchInputWithCustomKeyboardProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onClear?: () => void;
  className?: string;
}

/**
 * A search input component that opens a custom keyboard modal.
 * The input is read-only and opens the CustomKeyboardModal when clicked.
 * This works on both desktop and touch devices.
 */
export const SearchInputWithCustomKeyboard: React.FC<SearchInputWithCustomKeyboardProps> = ({
  value,
  onChange,
  placeholder = 'Search...',
  onClear,
  className = '',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleClear = () => {
    onChange('');
    onClear?.();
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // Always render read-only input that opens modal
  return (
    <>
      <div className={`flex items-center gap-2 bg-background border border-gray-300 rounded-lg px-3 py-2 ${className}`}>
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          readOnly
          inputMode="none"
          onClick={() => setIsModalOpen(true)}
          style={{
            backgroundColor: '#FFFFFF',
            color: '#1F2937',
            border: 'none',
            outline: 'none',
            cursor: 'pointer',
          }}
          className="flex-1 text-sm placeholder-gray-500 min-w-0"
        />
        {value && (
          <button
            onClick={handleClear}
            className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
          >
            <X size={16} className="text-gray-500" />
          </button>
        )}
      </div>

      <CustomKeyboardModal
        value={value}
        onChange={onChange}
        onClose={handleModalClose}
        placeholder={placeholder}
        isOpen={isModalOpen}
      />
    </>
  );
};
